import React, { useRef, useMemo, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  IGetRowsParams,
  ModuleRegistry,
  AllCommunityModule,
  themeMaterial,
  GridApi,
  GridReadyEvent,
} from "ag-grid-community";
import { useSelectorTyped as useSelector } from "../../../redux/common";
import { selectDetectionDownloadsInProgress } from "../../../redux/detections/detection-selectors";
import { DetectionFilterInput } from "../../../redux/detections/detection-types";
import * as d3 from "d3";
import FlexRow from "../../common/FlexRow";
import IconMenuHoriz from "../../common/IconMenuHoriz";
import ResizableSplitPanel from "../../common/ResizableSplitPanel";
import AnimalDetailsPanel from "../../animals/AnimalDetailsPanel";
import { DeploymentPanel } from "./DeploymentPanel";
import { DETECTION_DOWNLOAD_LIMIT } from "../detection-consts";
import { GetApp as GetAppIcon } from "@mui/icons-material";
import { ViewColumn as ViewColumnIcon } from "@mui/icons-material";
import { Popover, Checkbox, ListItemText, MenuItem } from "@mui/material";
import { Box, Typography, Button } from "@mui/material";
import FlexCol from "../../common/FlexCol";
import { Animal } from "../../../redux/animals/animals-types";
import { Deployment } from "../../../redux/deployments/deployments-types";
import { BaseColumnField, ParsedCsvRow } from "./types";
import { agGridSortToOrderBy, csvRowToTableRow } from "./helper";
import { allDetQuery } from "./helper";
import {
  baseColumnDefs,
  defaultCellRenderer,
  BASE_CELL_STYLE,
  allColumnIds,
  featCols,
  ltcFeatCols,
  ltcDefaultHiddenCol,
} from "./columns-and-cells";

// Ensure we're using just community AG features
ModuleRegistry.registerModules([AllCommunityModule]);

const ErrorOverlay = ({ onRetry }: { onRetry: () => void }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
    <Typography fontWeight={500} fontSize={18} mb={2}>
      Failed to fetch table data.
    </Typography>
    <Button
      variant="contained"
      color="primary"
      onClick={onRetry}
      sx={{ fontSize: 15, px: 3, py: 1 }}
    >
      Reload
    </Button>
  </Box>
);

export const DetectionTableAG: React.FC<{
  totalRowCount: number;
  columnFilters: DetectionFilterInput;
  includeMetadata: boolean;
  useTimeCorrection: boolean;
  downloadDetections: () => void;
  animals: Animal[];
  fileIdToNameMap: Map<string, string>;
}> = ({
  totalRowCount,
  columnFilters,
  downloadDetections,
  animals,
  fileIdToNameMap,
  includeMetadata,
  useTimeCorrection,
}) => {
  // AG Grid API ref
  const gridApi = useRef<GridApi | null>(null);

  const [gridError, setGridError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<BaseColumnField[]>(allColumnIds);
  const [columnWidths, setColumnWidths] = useState<Record<BaseColumnField, number>>(
    {} as Record<BaseColumnField, number>
  );
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const [panelMode, setPanelMode] = useState<"animal" | "deployment">("animal");
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);

  const deployments = useSelector(state => state.deployments.deployments);
  const dlInProgress = useSelector(selectDetectionDownloadsInProgress);

  // Handle animal cell click to show details and expand panel
  const handleCellClick = (event: any) => {
    const field = event.colDef?.field;
    const cellValue = event.value;

    // If clicking on animal name with animal data and a populated value
    // name will not be populated if there are conflicts
    if (field === "animal_name" && cellValue) {
      setSelectedAnimal(event.data.animals[0] || null);
      setPanelMode("animal");
      setSelectedDeployment(null);
      setIsPanelCollapsed(false);
    }
    // Open panel if single deployment linked to this detection
    else if (field === "stations" && event.data.deployments?.length == 1) {
      setSelectedDeployment(event.data.deployments[0] || null);
      setPanelMode("deployment");
      setSelectedAnimal(null);
      setIsPanelCollapsed(false);
    } else {
      setSelectedAnimal(null);
      setSelectedDeployment(null);
      setIsPanelCollapsed(true);
    }
  };

  // Memoize columnDefs with widths from state
  const columnDefs = useMemo(() => {
    let filteredCols = [...baseColumnDefs];
    if (!includeMetadata) {
      filteredCols = filteredCols.filter(col => !featCols.includes(col.field as BaseColumnField));
    }

    if (!useTimeCorrection) {
      filteredCols = filteredCols.filter(
        col => !ltcFeatCols.includes(col.field as BaseColumnField)
      );
    }

    return filteredCols.map(col => ({
      ...col,
      width: columnWidths[col.field!] || col.width,
    }));
  }, [columnWidths, includeMetadata, useTimeCorrection]);

  // Col selection handling using AG Grid API and local state
  const handleColumnToggle = (colId: BaseColumnField, forceVisiblity?: boolean) => {
    if (!gridApi.current) {
      console.error("No gridApi");
      return;
    }

    const column = gridApi.current.getColumnDef(colId) ? gridApi.current.getColumn(colId) : null;
    const currentlyVisible = column ? column.isVisible() : true;

    // Prevent hiding all columns
    const visibleCols = gridApi.current.getAllDisplayedColumns();
    if (currentlyVisible && visibleCols.length === 1) return;

    // Set grid state
    gridApi.current.setColumnsVisible(
      [colId],
      forceVisiblity != undefined ? forceVisiblity : !currentlyVisible
    );
    // Set our column state
    setVisibleColumns(prev => {
      if (currentlyVisible) {
        return prev.filter(id => id !== colId);
      } else {
        return [...prev, colId];
      }
    });

    // There is no direct way for the user to toggle time correction icon directly
    // Have it coupled to the "Time" column
    if (colId === "time_utc") {
      handleColumnToggle("warning");
    }
  };

  // Column picker
  const handleOpenColumnMenu = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };
  const handleCloseColumnMenu = () => {
    setColumnMenuAnchor(null);
  };

  const getCurrentVisibleColumns = (api: GridApi): BaseColumnField[] => {
    return allColumnIds.filter(colId => {
      const col = api.getColumn(colId);
      return col ? col.isVisible() : true;
    }) as BaseColumnField[];
  };

  const getCurrentColumnWidths = (api: GridApi): Record<BaseColumnField, number> => {
    const newWidths = {} as Record<BaseColumnField, number>;
    allColumnIds.forEach(colId => {
      const col = api.getColumn(colId as string);
      if (col) {
        newWidths[colId] = col.getActualWidth();
      }
    });
    return newWidths;
  };

  // On column move or visibility change, update visibleColumns state
  const onColumnVisible = () => {
    if (!gridApi.current) return;
    setVisibleColumns(getCurrentVisibleColumns(gridApi.current));
  };

  // Handle column resize and persist width
  const onColumnResized = (event: any) => {
    if (!event.finished) return; // Only update when user finishes resizing
    if (!gridApi.current) return;
    setColumnWidths(getCurrentColumnWidths(gridApi.current));
  };

  // Create a stable datasource object
  const datasource = useMemo(
    () => ({
      rowCount: totalRowCount,
      getRows: async (params: IGetRowsParams) => {
        setGridError(false);
        const gqlOrderBy = agGridSortToOrderBy(params.sortModel);
        const start = params.startRow;
        const limit = params.endRow - params.startRow;
        try {
          const resDet = await allDetQuery(
            start,
            limit,
            columnFilters,
            gqlOrderBy,
            includeMetadata,
            useTimeCorrection
          );
          // Everything will be parsed as string directly from the csv response
          // empties will = ""
          const dataRaw = d3.csvParse(resDet.allDetections.data) as ParsedCsvRow[];
          const data = dataRaw.map(r => csvRowToTableRow(r, animals, deployments, fileIdToNameMap));
          params.successCallback(data, totalRowCount);
        } catch (error) {
          console.error(error);
          setGridError(true);
          params.failCallback();
        }
      },
    }),
    [
      totalRowCount,
      columnFilters,
      animals,
      deployments,
      fileIdToNameMap,
      includeMetadata,
      useTimeCorrection,
    ]
  );

  // If we toggle time correction:
  //  ON  - Show timec columns
  //  OFF - Show Device time
  useEffect(() => {
    if (gridApi.current) {
      setVisibleColumns(getCurrentVisibleColumns(gridApi.current));
      setColumnWidths(getCurrentColumnWidths(gridApi.current));
      if (useTimeCorrection) {
        handleColumnToggle("device_time_utc", false);
        // Without this call columns will end up far right of table
        gridApi.current.moveColumns(ltcFeatCols, 0);
      } else {
        handleColumnToggle("device_time_utc", true);
        // Without this call columns will end up far right of table
        gridApi.current.moveColumns(["device_time_utc"], 0);
      }
    }
  }, [useTimeCorrection]); // eslint-disable-line react-hooks/exhaustive-deps

  // On grid ready, store API refs and initialize visibleColumns and columnWidths state
  const onGridReady = (params: GridReadyEvent) => {
    gridApi.current = params.api;
    setVisibleColumns(getCurrentVisibleColumns(params.api));
    // Hide the non default columns
    if (useTimeCorrection) {
      ltcDefaultHiddenCol.forEach(c => handleColumnToggle(c, false));
    }
    setColumnWidths(getCurrentColumnWidths(params.api));
  };

  if (gridError) {
    return (
      <ErrorOverlay
        onRetry={() => {
          setGridError(false);
          setReloadKey(k => k + 1);
        }}
      />
    );
  }

  const tableContent = (
    <FlexCol fullWidth fullHeight>
      <FlexRow vAlign="center" itemSpacing={2}>
        <IconMenuHoriz
          items={[
            {
              icon: <GetAppIcon />,
              onClick: downloadDetections,
              hoverText:
                dlInProgress.length > 0
                  ? "Please wait for the current download to finish."
                  : (totalRowCount || 0) > DETECTION_DOWNLOAD_LIMIT
                    ? `Please download less than ${DETECTION_DOWNLOAD_LIMIT?.toLocaleString()} detections at a time.`
                    : `Download CSV of all detections in selected filter (${totalRowCount?.toLocaleString()})`,
              disabled:
                !totalRowCount ||
                dlInProgress.length > 0 ||
                totalRowCount > DETECTION_DOWNLOAD_LIMIT,
            },
            {
              hoverText: "Select visible columns",
              icon: <ViewColumnIcon />,
              onClick: handleOpenColumnMenu,
            },
          ]}
        />
        {/* Anchored to "Select columns in IconMenuHoriz" */}
        <Popover
          open={Boolean(columnMenuAnchor)}
          anchorEl={columnMenuAnchor}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <div style={{ minWidth: 160, padding: 4 }}>
            {allColumnIds.map(colId => {
              const isVisible = visibleColumns.includes(colId);
              // Find the columnDef for this colId to get headerName
              const colDef = columnDefs.find(cd => cd.field === colId || cd.colId === colId);
              if (colDef && colDef.field !== "warning") {
                const label = colDef.headerName;
                return (
                  <MenuItem
                    key={colId}
                    onClick={() => handleColumnToggle(colId)}
                    dense
                    sx={{ minHeight: 32, paddingY: 0, paddingX: 0 }}
                  >
                    <Checkbox checked={isVisible} size="small" sx={{ padding: 0.5 }} />
                    <ListItemText primary={label} sx={{ margin: 0 }} />
                  </MenuItem>
                );
              }
            })}
          </div>
        </Popover>
      </FlexRow>
      <FlexCol fullHeight style={{ paddingRight: 16 }}>
        <AgGridReact
          theme={themeMaterial}
          key={reloadKey}
          maintainColumnOrder
          alwaysShowVerticalScroll
          rowHeight={32}
          headerHeight={40}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            cellRenderer: defaultCellRenderer,
            cellStyle: BASE_CELL_STYLE,
            headerStyle: BASE_CELL_STYLE,
          }}
          onColumnVisible={onColumnVisible}
          onColumnResized={onColumnResized}
          enableCellTextSelection={true} // Require to copy text from table
          tooltipShowDelay={100}
          onCellClicked={handleCellClick}
          // Data fetch config
          rowModelType="infinite"
          pagination={false}
          cacheBlockSize={1000}
          maxBlocksInCache={10}
          maxConcurrentDatasourceRequests={4}
          blockLoadDebounceMillis={200}
          datasource={datasource}
          //

          onGridReady={(params: GridReadyEvent) => {
            onGridReady(params);
          }}
        />
      </FlexCol>
    </FlexCol>
  );

  if (!includeMetadata) {
    return tableContent;
  }

  return (
    <ResizableSplitPanel
      direction="horizontal"
      firstInit="70%"
      firstMin="60%"
      secondMin="25%"
      firstName="table"
      secondName="details"
      secondCollapsedExternal={isPanelCollapsed}
      onSecondCollapsedChange={setIsPanelCollapsed}
      firstContent={tableContent}
      secondContent={
        panelMode === "animal" ? (
          <AnimalDetailsPanel
            animalsSelected={selectedAnimal ? [selectedAnimal] : []}
            onEventDelete={() => {}}
            onEventEdit={() => {}}
            deleteMeasurementSet={() => {}}
            editMeasurementSet={() => {}}
            onAnimalEdit={() => {}}
            onSubObjAdd={() => {}}
            readOnly={true}
          />
        ) : (
          <DeploymentPanel deployments={selectedDeployment ? [selectedDeployment] : []} />
        )
      }
    />
  );
};
